<template>
  <dn-collection
    v-if="!isSkipModuleEnable"
    :cid="id"
    :provider="provider"
    :options="options"
    class="dn-job-collection"
  >
    <template #default="data">
      <div
        v-if="shouldDisplayHeader(data.collection)"
        class="dn-job-header"
      >
        <dn-headline
          v-if="header.headline"
          tag="h3"
          class="header"
        >
          <dn-link
            :href="header.headline.url"
            :external="true"
          >
            <span>{{ header.headline.text }}</span>
            <dn-icon
              v-if="header.headline.icon"
              :name="header.headline.icon"
              class="dn-media-group"
            />
          </dn-link>
        </dn-headline>
        <div
          v-if="header.button1 || header.button2"
          class="dn-job-button"
        >
          <dn-link
            v-if="header.button1"
            :href="header.button1.url"
            :external="true"
            class="outline"
            :text="header.button1.text"
            target="_blank"
          />
          <dn-link
            v-if="header.button2"
            :href="header.button2.url"
            :external="true"
            class="outline"
            :text="header.button2.text"
            target="_blank"
          />
        </div>
      </div>

      <dn-live-wrapped-ad
        v-if="jobAd && jobAd.enabled"
        :id="jobAd.id"
        :min-height="getAdMinHeight(jobAd)"
      />

      <div class="dn-job-carousel">
        <dn-carousel
          v-if="data.collection?.length"
          align="prev"
          :circular="true"
          :adaptive="false"
          :breakpoints="breakpoints"
          :should-show-arrow="false"
          :should-show-pagination="true"
          pagination-type="scroll"
          :should-auto-play="true"
        >
          <template
            v-for="(item, index) in data.collection"
            :key="index"
          >
            <dn-link
              :href="item.detailsUrl || item.jobDetailsUrl"
              class="dn-teaser-card dn-job-item"
              :external="true"
              target="_blank"
            >
              <dn-card :title="item.title || item.jobTitle">
                <template #assets>
                  <dn-image
                    :src="item.logoUrl"
                    ratio="free"
                    :alt="item.recruiterName"
                    :original-width="0"
                    :original-height="0"
                    :point-of-interest="{ centerX: 0.5, centerY: 0.5 }"
                  />
                </template>
                <template #kicker>
                  <span>{{ item.recruiterName }}</span>
                </template>
              </dn-card>
            </dn-link>
          </template>
        </dn-carousel>
      </div>
    </template>
  </dn-collection>
</template>

<script setup lang="ts">
import { ref } from 'vue';

const { skipModule, moduleNames } = useSkipModules(useRoute());
const isSkipModuleEnable = ref(skipModule(moduleNames.JOB));

const props = defineProps({
  id: {
    type: String,
    required: true
  },
  provider: {
    type: String,
    required: true
  },
  options: {
    type: Object,
    required: true
  },
  header: {
    type: Object,
    required: false,
    default: null
  },
  jobAd: {
    type: Object,
    required: false,
    default: null
  }
});

const breakpoints = {
  0: {
    panelsPerView: '1.5'
  },
  768: {
    panelsPerView: '3'
  },
  1024: {
    panelsPerView: '4'
  }
};
function shouldDisplayHeader (collection: Array<any>) {
  return props.header && ((collection && collection.length > 0) || (props.jobAd && props.jobAd.enabled));
}
</script>

<style lang="scss">
.dn-job-collection {
  --dn-collection_fill: var(--dn-job-collection_fill, #f1f3f6);
  --dn-collection_spacing: var(--dn-job-collection_spacing, 16px);
  --dn-live-wrapped-ad_width: auto;
  --dn-live-wrapped-ad_padding: 18px;

  .header {
    --dn-headline_font-family: var(--dn-job-collection--header_font-family);
    --dn-headline_font-weight: var(--dn-job-collection--header_font-weight, 500);
    --dn-headline_font-size: var(--dn-job-collection--header_font-size, 20px);
    --dn-headline_line-height: var(--dn-job-collection--header_line-height, 100%);
    --dn-headline_color: var(--dn-job-collection--header_color, #232528);
    --dn-link_font-family: var(--dn-job-collection--header_font-family);
    --dn-link_font-size: var(--dn-job-collection--header_font-size, 20px);

    .dn-icon {
      --dn-icon_color: var(--dn-job-collection--icon_color, #232528);
    }
  }

  .dn-live-wrapped-ad,
  .dn-job-header {
    inline-size: auto;
    border-bottom: 1px solid var(--color-ui-generic-divider);
    padding: 0 0 var(--row-gap) 0;
  }

  .dn-job-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .dn-job-button {
      display: flex;
      gap: var(--row-gap);

      @media only screen and (width < 468px) {
        display: none;
      }
    }
  }

  .dn-job-carousel {
    --_dn-job-carousel-desktop_padding-multiply: var(--dn-job-carousel-desktop_padding-multiply, 2);
    --_dn-job-carousel-mobile_padding-multiply: var(--dn-job-carousel-mobile_padding-multiply, 4);

    inline-size: min(
      calc(var(--dn-collection-content-width, 980px) - var(--site-padding) * var(--_dn-job-carousel-desktop_padding-multiply)),
      calc(100vw - var(--site-padding) * var(--_dn-job-carousel-mobile_padding-multiply))
    );
    margin: auto;

    picture {
      --dn-image_aspect-ratio: 2 / 1;

      display: flex;
      align-items: center;
      background: var(--color-brand-utility-0);
    }

    .dn-teaser-card {
      &.dn-job-item {
        --dn-teaser-card--link_display: grid;
        --dn-teaser-card--card_grid-template: auto / repeat(12, 1fr);
        --dn-card_assets_grid-area: 1 / 1 / 6 / 13;
        --dn-card_title_grid-area: auto / 1 / span 1 / 13;
        --dn-card_kicker_grid-area: auto / 1 / span 1 / 13;
        --dn-teaser-card--card_padding: 0 0 var(--teaser-spacing);
        --dn-teaser-card--link-color: var(--color-teaser-standard-fg);
        --dn-teaser-card--title_font-size: var(--teaser-medium-min-font-size);
        --dn-teaser-card--title_line-height: var(--teaser-line-height);
        --dn-teaser-card--kicker_font-size: 10px;
        --dn-teaser-card--kicker_color: var(--color-ui-generic-subtle-fg);
        --dn-teaser-card--kicker_line-height: 110%;
        --dn-teaser-card--kicker_text-transform: uppercase;
        --dn-teaser-card--card_border-bottom-width: 1px;
        --dn-teaser-card--card_border-bottom-color: var(--color-ui-generic-divider);
        --dn-teaser-card--card_column-gap: 12px;

        &:not(.opinion) {
          --dn-teaser-card--title_font-family: var(--typography-card-small-font-family);
        }

        .brand &,
        .highlight &,
        .inverted &,
        .opinion &,
        .brand-secondary &,
        .feature & {
          --dn-teaser-card--card_background: none;
          --dn-teaser-card--link-color: var(--color-teaser-standard-fg);
          --dn-teaser-card--kicker_color: var(--color-ui-generic-subtle-fg);
          --dn-teaser-card--card_border-bottom-color: var(--color-ui-generic-divider);
        }
      }
    }
  }
}
</style>
